/*
 * home-hero-runtime.js
 * Native replacement for what Instant's `instant-core.min.js` does in the homepage hero (sections/home-hero.liquid):
 * the only behaviour there is the logo ticker.
 *
 * instant-core, measured on the live page (5 Oct 2026): it copies the logos of the first `.instant-ticker` into the
 * empty second one and writes on the ticker host
 *   --gap: <host column-gap>; --play: running; --direction: normal|reverse; --duration: <track width / speed>s;
 *   --pause-on-hover: running|paused; --multiplier: 1
 * after which the stylesheet's keyframes (`instant-ticker-instant-scroll-…`) scroll both tracks by one track width
 * plus one gap, forever. We do exactly that, and redo the variables on resize (the gap is 88px desktop / 40px mobile).
 *
 * COEXISTENCE: while the Instant app embed is installed, instant-core initialises this markup itself. This runtime
 * STANDS DOWN whenever instant-core actually ran and only takes over when it did not (app removed, or blocked).
 * The embed sets window.Instant.initialized even when the core never loads — that flag is never used as evidence.
 *
 * Sets window.__homeHeroMode = 'instant' | 'native' and window.__homeHeroNativeReady = true when wired.
 */
(function () {
  'use strict';
  if (window.__homeHeroInit) return;
  window.__homeHeroInit = true;

  function $all(sel, ctx) { try { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); } catch (e) { return []; } }
  function safe(fn) { return function () { try { return fn.apply(this, arguments); } catch (e) { /* never throw */ } }; }

  function tracksOf(host) {
    return Array.prototype.filter.call(host.children, function (c) { return c.classList.contains('instant-ticker'); });
  }

  function measure(host) {
    var tracks = tracksOf(host);
    if (!tracks.length) return;
    var speed = parseFloat(host.getAttribute('data-instant-ticker-speed')) || 24;
    var gap = getComputedStyle(host).columnGap || '0px';
    host.style.setProperty('--gap', gap);
    host.style.setProperty('--play', 'running');
    host.style.setProperty('--direction', host.getAttribute('data-instant-ticker-is-reversed') === 'true' ? 'reverse' : 'normal');
    host.style.setProperty('--duration', (tracks[0].scrollWidth / speed) + 's');
    host.style.setProperty('--pause-on-hover', host.getAttribute('data-instant-ticker-pause') === 'true' ? 'paused' : 'running');
    host.style.setProperty('--multiplier', '1');
  }

  function initTicker(host) {
    if (host.__homeHeroTicker) return;
    host.__homeHeroTicker = true;
    var tracks = tracksOf(host);
    if (tracks.length < 2) return;
    var seed = tracks[0].querySelector('.instant-ticker-initial-child-container');
    if (seed && !tracks[1].children.length) {
      Array.prototype.forEach.call(seed.children, function (c) {
        var copy = c.cloneNode(true);
        $all('img', copy).forEach(function (i) { i.setAttribute('alt', ''); });
        tracks[1].appendChild(copy);
      });
      tracks[1].setAttribute('aria-hidden', 'true');
    }
    measure(host);
    // images arriving late change the track width, and with it the duration
    $all('img', tracks[0]).forEach(function (i) { if (!i.complete) i.addEventListener('load', safe(function () { measure(host); }), { once: true }); });
  }

  var hosts = function () { return $all('[data-home-hero-native] [data-instant-type="ticker"]'); };

  function coreRan() {
    var I = window.Instant;
    return !!(I && (typeof I.initSwiper === 'function' || I.initializedListeners || I.eventAbortController || I.api));
  }
  function standDown() { window.__homeHeroMode = 'instant'; }
  function takeOver() {
    window.__homeHeroMode = 'native';
    hosts().forEach(safe(initTicker));
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(safe(function () { hosts().forEach(measure); }), 100);
    });
    window.__homeHeroNativeReady = true;
  }
  function decide() {
    if (coreRan()) return standDown();
    if (!document.querySelector('script[src*="instant-core"]')) return takeOver();
    var tries = 0;
    (function poll() {
      if (coreRan()) return standDown();
      if (++tries > 15) return takeOver();   // ~1.5 s: the tag is there but blocked or failed
      setTimeout(poll, 100);
    })();
  }

  // `defer`, and after the app embed's (also deferred) instant-core tag in document order — so by now instant-core
  // has run if it loaded at all.
  decide();
})();
